(ns frontend.view.nev.person.view
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.features.plan :as plan]
   [common.intelligence.common :as ic]
   [common.localization :as loc]
   [common.schema.field :as field]
   [common.util :as cu]
   [frontend.actions.common :as common-actions]
   [frontend.db :as app-db]
   [frontend.logging :as logging]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.components :as nev-components]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.header :as header]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.view.entity-viewers.modules.associated-entities :as associated-entities]
   [frontend.view.entity-viewers.modules.fees :as fees]
   [frontend.view.entity-viewers.modules.risk.event :as risk-event]
   [frontend.view.entity-viewers.modules.risk.view :as risk-module]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [ui.dom-globals :as dg]
   [frontend.view.field-templates :as templates]
   [frontend.view.nev.events :as events]
   [frontend.view.not-found :as not-found]
   [frontend.view.syncing-data :as syncing]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.view.nev.view :as nev-2024]))

(defn toolbar-buttons
  [{:keys [entity]}]
  (let [entity-id (:db/id entity)
        flagged? (contains? @session/flagged-entity-ids
                            entity-id)
        entity-graph-feature? @(r/track session/entity-graph?)
        new-entity-viewer-preview-2024? @(r/track session/new-company-viewer-preview-2024?)]
    (concat
     (when new-entity-viewer-preview-2024?
       (zap-toolbar/new-entity-viewer-2024-toggle-and-divider))

     [(zap-toolbar/dropdown-button
       {:icon      :fa-star
        :on-select (partial common-actions/toggle-flag entity flagged?)
        :ord       1
        :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})]

     (when entity-graph-feature?
       [{:icon :fa-diagram-project
         :toolbar-button?  false
         :dropdown-action? true
         :primary-button?  false
         :on-select (fn [_]
                      (routing/set-view! [:entity-graph :id entity-id]))
         :ord -1
         :label (loc/tr :entity-graph)}])

     [(when-not (:common/disabled? entity)
        (zap-toolbar/delete-button entity [:persons-grid]))]

     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids [entity-id]})])))

(def fees-associated-entities-attributes
  [:common/related-entities-company
   :common/related-entities-oppo])

(def target-types
  (concat [:entity.type/oppo
           :entity.type/company
           :entity.type/investor]
          (when (session/sales-pipelines?)
            [:entity.type/sales-opportunity
             :entity.type/sale-agreement])
          (when (session/fundraising?)
            [:entity.type/fundraising-opportunity])
          (when (session/portfolio?)
            [:entity.type/holding])))

(defn header-top
  [{:keys [entity read-only?]}]
  [:div
   {:class "flex gap-2"}
   [:span.entity-logo-container.icon-container
    {:style {:padding-left "0"}}
    [:i
     {:class-name (fu/cs :fal (entity/default-icon entity))}]]
   [:div.nev-name-row-container
    [nev-components/name-view
     {:entity     entity
      :read-only? read-only?}]]])

(def default-field-defs
  [{:fields       {0 {:field :person/universal-email-address}}
    :field-layout :f-4}
   {:fields       {0 {:field :person/notes}}
    :field-layout :f-4}])

(defn header-board
  [{:keys [_entity-atom]}]
  (let [header-state (r/atom {:edit-layout? false})
        set-edit-layout! (fn [val] (swap! header-state assoc :edit-layout? val))]
    (fn [{:keys [entity-atom]}]
      (let [edit-layout? (:edit-layout? @header-state)
            entity @entity-atom
            entity-id (:db/id entity)
            field-defs-path [:person :header :field-defs]
            field-defs-timeout-id :person-field-defs
            field-defs (or (events/timeout-value field-defs-timeout-id
                                                 @(r/track session/get-view-config field-defs-path))
                           default-field-defs)
            overrides nil
            selected-fields (->> field-defs
                                 (map :fields)
                                 (mapcat vals)
                                 (keep :field))
            remove-fields #{}
            schema (templates/entity-type-template
                    {:entity-type     :entity.type/person
                     :selected-fields selected-fields
                     :tenant-schema   @session/tenant-schema
                     :account         @session/account
                     :role            @session/role
                     :remove-fields   remove-fields
                     :overrides       overrides})

            ;;test all fields:
            #_#_field-defs
              (->> schema
                   (map first)
                   (partition-all 4 4)
                   (map (fn [fields]
                          (->> fields
                               (map-indexed (fn [i f]
                                              [i {:field f}])))))
                   (map (partial into {}))
                   (map (fn [fields]
                          {:fields       fields
                           :field-layout :f-1-1-1-1}))
                   vec)]
        [:div
         [header/header-board
          {:top              [header-top {:entity entity}]
           :preview-top      [header-top
                              {:entity     entity
                               :read-only? true}]
           :actions          (toolbar-buttons {:entity entity})
           :entity-id        entity-id
           :entity-atom      entity-atom
           :edit-layout?     edit-layout?
           :set-edit-layout! set-edit-layout!
           :field-defs       field-defs
           :schema           schema
           :save-layout!     (fn [new-field-defs]
                               (events/save-field-defs! field-defs-timeout-id
                                                        field-defs-path
                                                        new-field-defs
                                                        (fn [])
                                                        (fn [err]
                                                          (logging/error
                                                           "Failed to save field layout"
                                                           err))))}]]))))

(defn new-header?
  []
  (and (session/has-feature-access? :feature/customizable-nev)
       @session/customizable-nev?))

(defn person-view
  [{:keys [person-id]}]
  (let [initialized? (r/atom false)
        state-atom (r/cursor app-db/app-state [:views :nev :person])
        enrichment-state (r/cursor app-db/app-state [:views :enrichment :person])
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [person-id])]
    (r/create-class
     {:component-did-update
      (partial entity-utils/component-did-update
               :person-id
               (partial events/clear-errors! error-atom notification-atom))
      :component-did-mount
      (fn [_]
        (entity-utils/reset-state! state-atom person-id
                                   {:always-fetch? (session/new-grid-2023?)})
        (reset! initialized? true)
        (when (and (session/risks-access?)
                   (f-permissions/field-read-access? :person/risk-entities
                                                     :entity.type/person
                                                     nil))
          (rf/dispatch [::risk-event/load-risks person-id])))
      :component-will-unmount
      (fn [_]
        (events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [person-id] :as view-args}]
        (let [person (get @session/entities person-id)
              enriched-entity (ic/enrich-entity person)
              enrich-datas
              (->> person
                   (ic/enrichment-entities)
                   (filter (fn [ie]
                             (let [source (:intelligence-data/source ie)]
                               (case source
                                 :clearbit
                                 (plan/available :feature/enrichment-clearbit @session/tenant)
                                 :crunchbase
                                 (plan/available :feature/enrichment-cruchbase @session/tenant)
                                 false))))
                   (seq))]
          (cond
            (not @initialized?)
            [syncing/view]
            (or (:common/disabled? person)
                (:not-found @state-atom))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not person)
                    (:common/disabled? person))
              [:div " "]
              (let [tenant-schema @session/tenant-schema
                    related-sales-opportunities (when (session/sales-pipelines?)
                                                  (->> @session/all-sales-opportunities
                                                       (cu/filter-by :sales-opportunity/customer person-id)
                                                       (cu/remove-by :sales-opportunity/status :sales-opportunity.status/lost)
                                                       (cu/remove-by :sales-opportunity/status :sales-opportunity.status/won)
                                                       (remove :common/disabled?)))
                    related-sale-agreements (when (session/sales-pipelines?)
                                              (->> @session/all-sale-agreements
                                                   (cu/filter-by :sale-agreement/customer person-id)
                                                   (remove :common/disabled?)))
                    view-id :person
                    module-collapsed-path (fn [module-id]
                                            [view-id :modules :module-config module-id
                                             :collapsed?])]
                [:<>
                 (when-not (session/is-guest?)
                   [zap-toolbar/nev-toolbar
                    {:state           state-atom
                     :label           (partial zap-toolbar/toolbar-back
                                               [:persons-grid]
                                               (loc/tr :back-to/persons-grid))
                     :actions         (toolbar-buttons (assoc view-args
                                                              :entity
                                                              person))
                     :dropdown-toggle [:span (loc/tr :actions)
                                       [:i.fas.fa-angle-down.ml-2]]
                     :dropdown-toggle-class-name
                     "zap-button zap-button--primary-btn actions-dropdown"}])
                 [:div.entity__container
                  (when @error-atom
                    [nev-components/error-bar error-atom])
                  (when @notification-atom
                    [nev-components/notification-bar notification-atom])

                  [:div.entity
                   [:div
                    {:class "w-full max-w-[1920px] mx-auto p-4"}
                    (if (new-header?)
                      [header-board {:entity-atom entity-atom}]
                      [nev-components/header-module
                       {:component-state temp-atom
                        :view-state      view-state-atom
                        :enriched-entity enriched-entity
                        :entity-atom     entity-atom
                        :logo-url        (:person/avatar-url enriched-entity)
                        :email-attribute :person/universal-email-address
                        :entity          person
                        :selected-fields [:person/description
                                          :person/notes]}])
                    [:div.md:grid.grid-cols-10.gap-4
                     [:div
                      {:class (if (and (session/event-pane-disabled?)
                                       (not enrich-datas))
                                "col-span-10"
                                "col-span-10 lg:col-span-5")}
                      [:div.entity-left
                       [modules/view
                        {:view-id             view-id
                         :ordering            (get-in @session/view-config [view-id :modules :ordering])
                         :save-modules-order! (partial events/save-modules-order!
                                                       (:entity/type person))
                         :modules
                         (concat
                          [(merge
                            {:module-id              :person-information
                             :header                 (loc/tr :nev-module/about-this-entity-type
                                                             [(string/lower-case (loc/tr (:entity/type person)))])
                             ;;TODO uncomment when finished:
                             #_:header-tools
                             #_(when (session/is-admin?)
                                 [:span.entity-editor-header-settings
                                  {:on-click (fn [e])}
                                  ;;open settings

                                  [:span (loc/tr :entity-editor/settings-label)]
                                  [:i.fal.fa-cog]])
                             :collapsed?             (get-in @session/view-config
                                                             (module-collapsed-path :person-information))
                             :save-module-collapsed! (partial events/save-module-collapsed!
                                                              :entity.type/person)
                             :content                [:div
                                                      [entity-editor/view
                                                       {:state-atom    state-atom
                                                        :entity-id     person-id
                                                        :view-id       view-id
                                                        :module-id     :person-information
                                                        :remove-fields (concat [:person/universal-email-address
                                                                                :person/avatar-url
                                                                                :person/notes
                                                                                :person/gender
                                                                                :common/network-drive :common/named-network-drives]
                                                                               (field/entity-tag-fields
                                                                                tenant-schema
                                                                                (:entity/type person)))
                                                        :ordering      (get-in
                                                                        @session/view-config
                                                                        [view-id :modules :module-config
                                                                         :person-information :ordering])}]]}
                            (when (:person/location-city person)
                              {:header-tools
                               [:span.grow.flex.justify-end
                                [button/secondary-open
                                 {:tooltip (loc/tr :open-address-in-google-maps)}
                                 [:i.fal.fa-map-marker-alt
                                  {:on-click (fn [_]
                                               (let [person (get @session/entities person-id)]
                                                 (dg/open-window
                                                  (str "https://www.google.com/maps/place/"
                                                       (:person/location-address person)
                                                       ","
                                                       (when-let [zip (:person/location-zip-code
                                                                       person)]
                                                         (str zip "+"))
                                                       (:person/location-city person)))))}]]]}))
                           (when (and (session/risks-access?)
                                      (f-permissions/field-read-access? :person/risk-entities
                                                                        :entity.type/person
                                                                        nil))
                             (risk-module/risk-module-config
                              {:module-label           (loc/tr :nev-module/person-risks)
                               :module-id              :person-risks
                               :view-id                view-id
                               :nev-entity-type        :entity.type/person
                               :save-collapsed-status! (r/partial events/save-module-collapsed!
                                                                  (:entity/type
                                                                   :entity.type/person))
                               :entity-id              person-id
                               :add-new-button?        false}))

                           (let [module-id :person-investor
                                 order-config-path [view-id :modules :module-config
                                                    module-id :ordering]]
                             (nev-components/entity-relation-module
                              {:state-atom             state-atom
                               :module-id              module-id
                               :view-id                view-id
                               :ordering               (get-in
                                                        @session/view-config
                                                        order-config-path)
                               :order-config-path      order-config-path
                               :module-label           (loc/tr :nev-module/person-investor)
                               :entity                 person
                               :relationship-attribute :investor/legal-entity
                               :remove-fields          [:investor/legal-entity]
                               :reverse-relationship?  true}))]
                          (when-not (session/is-guest?)
                            (associated-entities/modules
                             {:entity-id              person-id
                              :save-module-collapsed! events/save-module-collapsed!
                              :module-collapsed-path  module-collapsed-path}))
                          (when (session/fee-tracking?)
                            (fees/modules {:entity-id              person-id
                                           :save-module-collapsed! events/save-module-collapsed!
                                           :module-collapsed-path  module-collapsed-path
                                           :fees-associated-entities-attributes
                                           fees-associated-entities-attributes}))
                          [(when (and (not (session/is-guest?))
                                      (session/sales-pipelines?)
                                      (seq related-sales-opportunities))
                             (nev-components/association-module-config
                              {:module-label           (loc/tr :nev-module/person-customer-in-sales-oppos)
                               :module-id              :customer-in-sales-oppos
                               :view-id                view-id
                               :nev-entity-type        :entity.type/person
                               :target-entity-type     :entity.type/sales-opportunity
                               :associated-entities    related-sales-opportunities
                               :save-collapsed-status! (partial events/save-module-collapsed!
                                                                (:entity/type person))
                               :add-new-button?        false}))
                           (when (and (not (session/is-guest?))
                                      (session/sales-pipelines?)
                                      (seq related-sale-agreements))
                             (nev-components/association-module-config
                              {:module-label           (loc/tr
                                                        :nev-module/person-customer-in-sale-agreements)
                               :module-id              :customer-in-sale-agreements
                               :view-id                view-id
                               :nev-entity-type        :entity.type/person
                               :target-entity-type     :entity.type/sale-agreement
                               :associated-entities    related-sale-agreements
                               :save-collapsed-status! (partial events/save-module-collapsed!
                                                                (:entity/type person))
                               :add-new-button?        false}))
                           (when (and (not (session/is-mobile?))
                                      (f-permissions/field-read-access? :common/attachments
                                                                        :entity.type/person
                                                                        nil))
                             (nev-components/attachments-module view-id :person-attachments person))])}]]]
                     (when-not (session/is-guest?)
                       [:div
                        {:class "col-span-10 lg:col-span-5"}
                        (when enrich-datas
                          [nev-components/enrichment-module
                           {:module-id              :person-enrichment-data
                            :collapsed?             @(r/track session/get-view-config
                                                              (module-collapsed-path :person-enrichment-data))
                            :state                  enrichment-state
                            :view-id                view-id
                            :save-module-collapsed! (r/partial events/save-module-collapsed!
                                                               :entity.type/person)
                            :entities               enrich-datas}])
                        [event-pane/view
                         {:state       events-pane-atom
                          :entity-ids  [person-id]
                          :module-args {:state-atom state-atom
                                        :view-id    view-id}}]])]]]]])))))})))

(defn new-person-view-2024
  [{:keys [person-id]}]
  (let [state-path [:nev-2024 :person person-id]]
    [:div
     ;; FIXME: height hack
     {:class "h-[calc(100vh-52px)] overflow-hidden"}
     [nev-2024/view-with-data-fetching
      {:entity-id   person-id
       :entity-type :entity.type/person
       :state-path  state-path}]]))

(defn person-view-wrapper
  [{:keys [person-id]}]
  (let [classic-entity-viewer-enabled? @(r/track session/classic-entity-viewer-enabled?)]
    (if classic-entity-viewer-enabled?
      [person-view {:person-id person-id}]
      [new-person-view-2024 {:person-id person-id}])))

(defmethod routing/render-view :entity.type/person
  [{:keys [route-params _view-params]}]
  (let [person-id (-> route-params
                      :id
                      js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page person"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "person-viewer-id-" person-id)}
     [person-view-wrapper {:person-id person-id}]]))
